import { makeCollapsible } from '@learnforge/demo-kit';
import type { I18n } from '@learnforge/i18n';

export interface ChartCardOptions<K extends string = string> {
  titleKey: K;
  i18n: I18n<K>;
  infoKey?: string;
  className?: string;
  headerActions?: readonly HTMLElement[];
  collapsible?: { storageKey?: string; initiallyOpen?: boolean };
}

export interface ChartCardHandle<K extends string = string> {
  element: HTMLElement;
  body: HTMLElement;
  setBody(node: Node): void;
  setTitleKey(key: K): void;
  setBusy(busy: boolean): void;
  destroy(): void;
}

export function createChartCard<K extends string = string>(
  opts: ChartCardOptions<K>,
): ChartCardHandle<K> {
  let titleKey = opts.titleKey;

  const element = document.createElement('div');
  element.className = `chart-card ${opts.className ?? ''}`.trim();
  element.setAttribute('aria-busy', 'false');

  const header = document.createElement('div');
  header.className = 'chart-card-header';

  const titleEl = document.createElement('div');
  titleEl.className = 'chart-card-title';
  header.appendChild(titleEl);

  if (opts.headerActions && opts.headerActions.length > 0) {
    const actions = document.createElement('div');
    actions.className = 'chart-card-actions';
    actions.append(...opts.headerActions);
    header.appendChild(actions);
  }

  const body = document.createElement('div');
  body.className = 'chart-card-body';

  element.append(header, body);

  if (opts.infoKey) {
    const info = document.createElement('button');
    info.type = 'button';
    info.className = 'chart-card-info';
    info.dataset['infoKey'] = opts.infoKey;
    info.textContent = '?';
    element.appendChild(info);
  }

  const collapsibleHandle = opts.collapsible
    ? makeCollapsible(element, {
        initiallyOpen: opts.collapsible.initiallyOpen ?? true,
        ...(opts.collapsible.storageKey ? { storageKey: opts.collapsible.storageKey } : {}),
        titleSelector: '.chart-card-header',
      })
    : null;

  function render() {
    titleEl.textContent = opts.i18n.t(titleKey);
  }
  render();
  const unsub = opts.i18n.subscribe(render);

  return {
    element,
    body,
    setBody(node) {
      body.replaceChildren(node);
    },
    setTitleKey(key) {
      titleKey = key;
      render();
    },
    setBusy(busy) {
      element.setAttribute('aria-busy', busy ? 'true' : 'false');
    },
    destroy() {
      collapsibleHandle?.destroy();
      unsub();
      element.remove();
    },
  };
}
